import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import { getAllSubscriptions } from '../../services/subscriptionService';
import { getUserEngagementInsights } from '../../services/analyticsService';
import { aggregateSubscriptions } from '../../services/subscriptionAggregator';
import { getRevenueAnalytics } from '../../services/revenueService';
import { buildDashboardContext, generateAIInsights } from '../../services/insightsContextService';

const InsightsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<any>(null);
  const [aiInsights, setAiInsights] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    const loadInsights = async () => {
      try {
        const calculatedInsights: string[] = [];

        // Get all data
        const [users, subscriptions, engagementInsights, revenueData] = await Promise.all([
          getCollection('users', undefined, undefined, undefined, 10000).catch(() => []),
          getAllSubscriptions().catch(() => []),
          getUserEngagementInsights().catch(() => null),
          getRevenueAnalytics(30).catch(() => null),
        ]);

        // Calculate key metrics
        const totalUsers = users.length;
        const activeSubscriptions = subscriptions.filter((s: any) => s.status === 'active').length;
        const subscriptionRate = totalUsers > 0 ? (activeSubscriptions / totalUsers) * 100 : 0;
        const mrr = revenueData?.totalRevenue || 0;
        const arpu = revenueData?.averageRevenuePerUser || 0;

        setMetrics({
          totalUsers,
          activeSubscriptions,
          subscriptionRate,
          mrr,
          arpu,
        });

        // Generate insights
        if (subscriptionRate < 5) {
          calculatedInsights.push(
            `⚠️ Low subscription conversion rate (${subscriptionRate.toFixed(2)}%). Consider optimizing the paywall experience or value proposition.`
          );
        } else if (subscriptionRate > 20) {
          calculatedInsights.push(
            `✅ Excellent subscription conversion rate (${subscriptionRate.toFixed(2)}%)! Your value proposition is resonating well with users.`
          );
        }

        if (engagementInsights) {
          const totalSearches = engagementInsights.totalSearches || 0;
          const totalTopicsAdded = engagementInsights.totalTopicsAdded || 0;
          const searchToAddRate = totalSearches > 0 ? (totalTopicsAdded / totalSearches) * 100 : 0;

          if (searchToAddRate < 10) {
            calculatedInsights.push(
              `⚠️ Low search-to-topic conversion (${searchToAddRate.toFixed(1)}%). Users are searching but not adding topics - consider improving topic suggestions or relevance.`
            );
          }

          if (engagementInsights.popularTopics && engagementInsights.popularTopics.length > 0) {
            const topTopic = engagementInsights.popularTopics[0];
            calculatedInsights.push(
              `📊 Most popular topic: "${topTopic.title}" (added ${topTopic.count} times). Consider creating more content in this category.`
            );
          }

          if (engagementInsights.searchWordCounts && engagementInsights.searchWordCounts.length > 0) {
            const topSearch = engagementInsights.searchWordCounts[0];
            calculatedInsights.push(
              `🔍 Top search term: "${topSearch.word}" (searched ${topSearch.count} times). Consider adding this as a featured topic or improving discoverability.`
            );
          }
        }

        if (mrr < 100) {
          calculatedInsights.push(
            `💰 Current MRR is $${mrr.toFixed(2)}. Focus on increasing active subscriptions to grow revenue.`
          );
        } else {
          calculatedInsights.push(
            `💰 Current MRR is $${mrr.toFixed(2)}. Revenue growth is on track.`
          );
        }

        if (arpu < 5) {
          calculatedInsights.push(
            `📈 ARPU is $${arpu.toFixed(2)}. Consider introducing higher-tier plans or upselling premium features.`
          );
        }

        // Platform insights
        const iosSubs = subscriptions.filter((s: any) => s.platform === 'ios').length;
        const androidSubs = subscriptions.filter((s: any) => s.platform === 'android').length;
        const stripeSubs = subscriptions.filter((s: any) => s.platform === 'stripe').length;

        if (iosSubs > androidSubs * 1.5) {
          calculatedInsights.push(
            `📱 iOS has significantly more subscriptions (${iosSubs}) than Android (${androidSubs}). Consider Android-specific marketing or feature parity.`
          );
        } else if (androidSubs > iosSubs * 1.5) {
          calculatedInsights.push(
            `📱 Android has significantly more subscriptions (${androidSubs}) than iOS (${iosSubs}). Consider iOS-specific marketing.`
          );
        }

        // User growth insights
        const recentUsers = users.filter((u: any) => {
          if (!u.createdAt) return false;
          const createdAt = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
          const daysSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
          return daysSinceCreation <= 30;
        }).length;

        if (recentUsers > 0) {
          const growthRate = (recentUsers / totalUsers) * 100;
          calculatedInsights.push(
            `📈 ${recentUsers} new users in the last 30 days (${growthRate.toFixed(1)}% of total). User acquisition is active.`
          );
        }

        setInsights(calculatedInsights);
      } catch (error) {
        console.error('Error generating insights:', error);
        setInsights(['Unable to generate insights at this time.']);
      } finally {
        setLoading(false);
      }
    };

    loadInsights();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Generating insights...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Business Intelligence & Insights
      </h1>

      {/* Key Metrics */}
      {metrics && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem',
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Users</p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
              {metrics.totalUsers}
            </h2>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Subscription Rate</p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
              {metrics.subscriptionRate.toFixed(2)}%
            </h2>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>MRR</p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
              ${metrics.mrr.toFixed(2)}
            </h2>
          </div>
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>ARPU</p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
              ${metrics.arpu.toFixed(2)}
            </h2>
          </div>
        </div>
      )}

      {/* Insights List */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Key Insights & Recommendations</h2>
        {insights.length > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {insights.map((insight, index) => (
              <li
                key={index}
                style={{
                  padding: '1rem',
                  marginBottom: '0.5rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  borderLeft: '4px solid #2563eb',
                }}
              >
                {insight}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ color: '#666' }}>No insights available at this time.</p>
        )}
      </div>

      {/* AI-powered insights */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '2rem',
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          AI Recommendations
        </h2>
        <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Generate actionable recommendations from an LLM using your current dashboard data. Requires <code>adminAnalyticsInsights</code> Cloud Function and <code>OPENAI_API_KEY</code> secret.
        </p>
        <button
          type="button"
          onClick={async () => {
            setAiError(null);
            setAiInsights(null);
            setAiLoading(true);
            try {
              const context = await buildDashboardContext({ days: 30, retentionDays: 90 });
              const result = await generateAIInsights(context);
              if (result.success) {
                setAiInsights(result.insights);
              } else {
                setAiError(result.error);
              }
            } catch (e: any) {
              setAiError(e?.message || 'Failed to generate insights');
            } finally {
              setAiLoading(false);
            }
          }}
          disabled={aiLoading}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: aiLoading ? '#94a3b8' : '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: aiLoading ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {aiLoading ? 'Generating…' : 'Generate AI insights'}
        </button>
        {aiError && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#fef2f2',
            borderRadius: '4px',
            color: '#b91c1c',
            borderLeft: '4px solid #dc2626',
          }}>
            {aiError}
          </div>
        )}
        {aiInsights && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: '#f8fafc',
              borderRadius: '4px',
              borderLeft: '4px solid #0ea5e9',
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: '0.9375rem',
              lineHeight: 1.6,
            }}
          >
            {aiInsights}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsTab;

